import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import SaveIcon from '@mui/icons-material/Save';
import Typography from '@mui/material/Typography';
import { i18n } from 'i18n';
import { AutoAssignStatusCounts } from './bulkImportSolidsSceneState';

interface WizardStepSummaryProps {
  counts: AutoAssignStatusCounts;
  savableCount: number;
  manualReviewResolvedCount: number;
  isSaving: boolean;
  onBack: () => void;
  onProceedToManualReview: () => void;
  onSaveAndFinish: () => void;
}

const WizardStepSummary = ({
  counts,
  savableCount,
  manualReviewResolvedCount,
  isSaving,
  onBack,
  onProceedToManualReview,
  onSaveAndFinish,
}: WizardStepSummaryProps) => {
  const manualReviewCount = counts.manual_review;
  const manualReviewRemaining = Math.max(manualReviewCount - manualReviewResolvedCount, 0);
  const autoAssignedCount = counts.mark_existing_node + counts.insert_between_nodes + counts.update_existing;
  const totalCount = autoAssignedCount + manualReviewCount + counts.skipped_existing;

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">{i18n._('Auto-Assign Summary')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {i18n._('{total} solids processed', { total: totalCount })}
      </Typography>

      <List dense>
        <ListItem>
          <ListItemText
            primary={i18n._('Matched to existing nodes')}
            secondary={i18n._('{count} solids', { count: counts.mark_existing_node })}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={i18n._('New nodes to insert on spools')}
            secondary={i18n._('{count} solids', { count: counts.insert_between_nodes })}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={i18n._('Existing stopes to update')}
            secondary={i18n._('{count} solids', { count: counts.update_existing })}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={i18n._('Need manual review')}
            secondary={i18n._('{count} solids, {resolved} reviewed', { count: manualReviewCount, resolved: manualReviewResolvedCount })}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={i18n._('Skipped (already exist)')}
            secondary={i18n._('{count} solids', { count: counts.skipped_existing })}
          />
        </ListItem>
      </List>

      <Divider />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {manualReviewCount > 0 && (
          <Button
            variant="contained"
            color="secondary"
            onClick={onProceedToManualReview}
            disabled={isSaving}
          >
            {manualReviewRemaining > 0
              ? i18n._('Manually Review {count}', { count: manualReviewRemaining })
              : i18n._('Review Again')}
          </Button>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={onSaveAndFinish}
          disabled={isSaving || savableCount === 0}
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {i18n._('Save and Finish')}
        </Button>
        <Button variant="text" color="secondary" onClick={onBack} disabled={isSaving}>
          {i18n._('Back')}
        </Button>
      </Box>
    </Box>
  );
};

export default WizardStepSummary;
